import { useEffect, useState } from 'react';

type ToastType = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  action?: ToastAction;
  durationMs?: number;
}

interface Toast {
  id: number;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

const DEFAULT_DURATION_MS = 3500;

let addToastFn: ((toast: Toast, durationMs: number) => void) | null = null;
let dismissToastFn: ((id: number) => void) | null = null;
let _toastCounter = 0;

/** Returns the toast's id, for `dismissToast`. */
export function toast(message: string, type: ToastType = 'info', options: ToastOptions = {}): number {
  const id = ++_toastCounter;
  addToastFn?.({ id, message, type, action: options.action }, options.durationMs ?? DEFAULT_DURATION_MS);
  return id;
}

export function dismissToast(id: number) {
  dismissToastFn?.(id);
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    addToastFn = (toast, durationMs) => {
      setToasts(t => [...t, toast]);
      setTimeout(() => setToasts(t => t.filter(x => x.id !== toast.id)), durationMs);
    };
    dismissToastFn = id => setToasts(t => t.filter(x => x.id !== id));
    return () => { addToastFn = null; dismissToastFn = null; };
  }, []);

  const icons = { success: '✓', error: '✕', info: 'ℹ' };
  const colors = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className="fixed bottom-20 md:bottom-4 right-4 z-[100] flex flex-col gap-2">
      {toasts.map(({ action, ...t }) => (
        <div key={t.id} className={`flex items-center gap-2 px-4 py-3 rounded-xl border shadow-card text-sm font-medium ${colors[t.type]}`}>
          <span>{icons[t.type]}</span>
          {t.message}
          {action && (
            <button
              type="button"
              onClick={() => { action.onClick(); dismissToastFn?.(t.id); }}
              className="ml-2 font-semibold underline underline-offset-2 cursor-pointer hover:opacity-80"
            >
              {action.label}
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
